import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Switch, Button, Space, Card, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import './detail-index.scss';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createQuestionSet,
  findQuestionSetById,
  resetDetail,
  updateQuestionSet,
} from '../../../../store/questions/questionSlice';
import { AppDispatch, RootState } from '../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { QuestionCreateReq } from '../../../../interface/question';
import { getAllVoucherList } from '../../../../store/vouchers/voucherSlice';
import { errorCode } from '../../../../constants/errorHandle';

const { Option } = Select;

const SurveyDetail: React.FC = () => {
  const questionSlice = useSelector((state: RootState) => state.question);
  const voucherSlice = useSelector((state: RootState) => state.voucher);
  const { detail } = questionSlice;
  const { data } = voucherSlice;
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [voucherCode, setVoucherCode] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<{ id: number; question: string; answers: string[] }[]>([
    {
      id: 1,
      question: '',
      answers: [],
    },
  ]);

  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (id) {
      dispatch(findQuestionSetById({ qsId: id }));
    }
    return () => {
      dispatch(resetDetail());
    };
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(getAllVoucherList());
  }, [dispatch]);

  useEffect(() => {
    if (detail && id) {
      form.setFieldsValue({
        type: detail.type === 5 ? '5日間' : '20日間',
        isActive: detail.isActive || false,
      });

      if (detail.questionList) {
        setQuestions(
          detail.questionList.map((q: any, index: number) => ({
            id: index + 1,
            question: q.question || '',
            answers: q.answers || [],
          })),
        );
      }
      setVoucherCode(detail.voucherCode || undefined);
    }
  }, [detail, form, id]);

  const addQuestion = () => {
    const newQuestion = {
      id: questions.length + 1,
      question: '',
      answers: [],
    };
    setQuestions([...questions, newQuestion]);
  };

  const addOption = (questionId: number) => {
    setQuestions((prev) => prev.map((q) => (q.id === questionId ? { ...q, answers: [...q.answers, ''] } : q)));
  };

  const removeOption = (questionId: number, index: number) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === questionId ? { ...q, answers: q.answers.filter((_, i) => i !== index) } : q)),
    );
  };

  const removeQuestion = (questionId: number) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  const onFinish = async (values) => {
    if (questions.length === 0) {
      message.error('少なくとも1つの質問を作成してください!');
      return;
    }
    const invalidQuestions = questions.filter((question) => question.answers.some((answer) => answer.trim() === ''));
    if (invalidQuestions.length > 0) {
      message.error('全ての質問に少なくとも1つの回答を追加してください!');
      return;
    }
    const formattedQuestions = questions.map((item) => ({
      id: item.id,
      question: item.question,
      answers: item.answers.map((ans) => ans),
    }));
    const payload: QuestionCreateReq = {
      type: values.type === '5日間' ? 5 : 20,
      isActive: values.isActive,
      voucherCode: voucherCode,
      questionList: formattedQuestions,
    };

    if (id) {
      const result = await dispatch(
        updateQuestionSet({
          qsId: id,
          ...payload,
        }),
      );

      if (result.meta.requestStatus === 'fulfilled') {
        message.success('更新が成功しました!');
        navigate('/survey-management');
      } else if (result.meta.requestStatus === 'rejected') {
        const errorMessage = (result.payload as any)?.error;
        const defaultErrorMessage = '更新に失敗しました。もう一度お試しください。';
        message.error(errorCode(errorMessage, defaultErrorMessage));
      }
    } else {
      const result = await dispatch(createQuestionSet(payload));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success('新規作成が成功しました!');
        navigate('/survey-management');
      } else {
        message.error('新規作成に失敗しました。もう一度お試しください!');
      }
    }
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <div className="survey-detail-container">
      <Button className="back-button" style={{ marginBottom: '10px' }} onClick={handleBack}>
        ←
      </Button>
      <Form
        layout="vertical"
        className="survey-detail-header"
        form={form}
        onFinish={onFinish}
        initialValues={{
          type: '5日間',
          isActive: true,
        }}
      >
        <Form.Item label="日間設定" name="type">
          <Select style={{ width: 300 }}>
            <Option value="5日間">5日間</Option>
            <Option value="20日間">20日間</Option>
          </Select>
        </Form.Item>
        <Form.Item label="状態" name="isActive" valuePropName="checked" labelAlign="left" style={{ width: '100%' }}>
          <Switch defaultChecked />
        </Form.Item>
        <div>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
          </Form.Item>
        </div>
      </Form>

      <Form layout="vertical">
        <Card className="voucher-card">
          <Form.Item label="バウチャーコード" className="voucher-item">
            <Select placeholder="バウチャー選択" value={voucherCode} onChange={(value) => setVoucherCode(value)}>
              {data.map((voucher, index) => (
                <Option key={index} value={voucher.id}>
                  {voucher.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Card>

        {questions.map((question) => (
          <Card
            key={question.id}
            title={`質問 ${question.id}`}
            extra={
              <Space>
                <Button
                  type="link"
                  icon={
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/icons/edit.png`}
                      alt="Edit Logo"
                      className="icon-table"
                    />
                  }
                />
                <Button
                  type="link"
                  icon={
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/icons/delete.png`}
                      alt="Delete Logo"
                      className="icon-table"
                    />
                  }
                  danger
                  onClick={() => removeQuestion(question.id)}
                />
              </Space>
            }
          >
            <Form.Item required>
              <Input.TextArea
                placeholder="質問入力"
                autoSize={{ minRows: 2, maxRows: 5 }}
                style={{ fontSize: '16px' }}
                value={question.question}
                onChange={(e) => {
                  const updatedTitle = e.target.value;
                  setQuestions((prev) =>
                    prev.map((q) => (q.id === question.id ? { ...q, question: updatedTitle } : q)),
                  );
                }}
              />
            </Form.Item>
            {question.answers.map((option, index) => (
              <Form.Item key={index} className="option-item" required>
                <Input
                  placeholder={`オプション ${index + 1}`}
                  value={option}
                  onChange={(e) => {
                    const updatedOptions = [...question.answers];
                    updatedOptions[index] = e.target.value;
                    setQuestions((prev) =>
                      prev.map((q) => (q.id === question.id ? { ...q, answers: updatedOptions } : q)),
                    );
                  }}
                  addonAfter={
                    <span
                      style={{
                        cursor: 'pointer',
                        color: 'black',
                        fontSize: '14px',
                      }}
                      onClick={() => removeOption(question.id, index)}
                    >
                      ×
                    </span>
                  }
                />
              </Form.Item>
            ))}
            <Button type="link" onClick={() => addOption(question.id)} icon={<PlusOutlined />}>
              回答追加
            </Button>
          </Card>
        ))}

        <Button type="dashed" block icon={<PlusOutlined />} onClick={addQuestion}>
          質問追加
        </Button>
      </Form>
    </div>
  );
};

export default SurveyDetail;
